import LoginForm from '@/components/AuthenticationSelect/components/LoginForm';
import { useOAuthButtons } from '@/components/AuthenticationSelect/components/OAuthButtons/useOAuthButtons';
import DocumentHeader from '@/components/DocumentHeader';
import { OneColumn } from '@/components/Layout';
import { Navigation } from '@/components/Navigation';
import { ContinueWithGoogle } from '@/components/OAuthButtons';
import { SWS_HOST, SWS_TERMS_AND_CONDITIONS } from '@/constants';
import { PUBLIC_SWS_ENV } from '@/constants/env';
import { ROUTE_USER_PROFILE } from '@/constants/routes';
import { useCookieContext } from '@/context/CookieProvider';
import { useFlag } from '@/features/unleash';
import { useHandleLoginResponse } from '@/hooks/useLogin/useHandleLoginResponse';
import { useUtmExperiment } from '@/hooks/usePlanUtmExperiment';
import { Translation } from '@/hooks/useTranslation';
import useUser from '@/hooks/useUser';
import { useUserAccess } from '@/hooks/useUserAccess';
import loadable from '@loadable/component';
import { getDecryptedEmail } from '@simplywallst/services';
import { Row } from '@simplywallst/ui-core';
import CrossSolid from '@simplywallst/ui-core/icons/CrossSolid';
import { Button, Heading, Modal } from '@simplywallst/ui-core/next';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTracking } from 'react-tracking';
import { redirect } from 'redux-first-router';
import { CompareFeatures } from './components/CompareFeatures/CompareFeatures';
import { EasterPromoLoginSplash } from './components/EasterPromoLoginSplash';
import { FeaturedOn } from './components/FeaturedOn/FeaturedOn';
import { Plans } from './components/Plans/Plans';
import { Reviews } from './components/Reviews/Reviews';
import { useAuthCodeLogin } from './hooks/useAuthCodeLogin';
import { useGoogleOneTap } from './hooks/useGoogleOneTap';
import { usePageTranslation } from './hooks/usePageTranslation';
import { UTM_GROUP, useUTMSearchParams } from './hooks/useUTMSearchParams';

const GuestRegistrationModal = loadable(
  () => import(/* webpackPrefetch: true */ '@components/GuestRegistrationModal')
);

const ZendeskWidget = loadable(() => import('./components/ZendeskWidget'), {
  resolveComponent: (c) => c.ZendeskWidget,
});

const Footer = loadable(() => import('@/components/Footer'), {
  resolveComponent: (c) => c.Footer,
});

const Faqs = loadable(() => import('./components/Faqs'), {
  resolveComponent: (c) => c.Faqs,
});

let once = false;

const OurPlans = () => {
  const { Track, trackEvent } = useTracking({
    type: 'track',
    subject: 'page',
    page: 'plans',
  });
  const { plansVariant } = useCookieContext();
  const t = usePageTranslation();

  const { enabled: enableZendeskWidget, flagsReady } = useFlag({
    name: 'zendesk-widget',
  });
  const { data: userData, queryStatus, mutate } = useUser();
  const hasSeenPlansRef = useRef<boolean | undefined>(undefined);

  const dispatch = useDispatch();
  const { data: userAccess } = useUserAccess();
  const isAvanzaUser = userAccess.brokerReferrer === 'avanza';

  useEffect(() => {
    // Avanza users shouldn't be able to change their plan or manage subscriptions, as this is controlled by Avanza.
    if (isAvanzaUser) {
      dispatch(redirect({ type: ROUTE_USER_PROFILE }));
      return;
    }
  }, [dispatch, isAvanzaUser]);

  const { hasSeenPlansPage } = userData;

  useEffect(() => {
    if (!hasSeenPlansPage) {
      mutate({
        settings: {
          has_seen_plans_page: true,
        },
      });
    }

    if (typeof hasSeenPlansRef.current === 'undefined') {
      hasSeenPlansRef.current = hasSeenPlansPage;
    }
    if (queryStatus === 'success' && flagsReady && !once) {
      // wait for user data to be loaded
      trackEvent({
        action: 'landed',
        variant: plansVariant,
        showZendesk: enableZendeskWidget && hasSeenPlansPage,
      });
      once = true;
    }
  }, [
    hasSeenPlansPage,
    queryStatus,
    mutate,
    flagsReady,
    enableZendeskWidget,
    plansVariant,
  ]);

  const { isUtmExperiment } = useUtmExperiment();
  const isUtmExperimentEnabled = isUtmExperiment && !userData.loggedIn;

  const [isPromoModalVisible, setIsPromoModalVisible] = useState(
    isUtmExperimentEnabled
  );

  const utmParams = useUTMSearchParams();

  const { handleLoginResponse } = useHandleLoginResponse();

  const authCodeLogin = useAuthCodeLogin({
    code: utmParams.authCode,
    enabled:
      !userData.loggedIn &&
      // Keep backwards compatibility (for now) with existing google auth flow, if specified.
      utmParams.authType !== 'google',
    onResolve: (data) => {
      handleLoginResponse({
        data,
        provider: 'sws',
        // Keep the user on the plans page.
        redirectOnPostLogin: false,
      });
      setIsPromoModalVisible(false);
    },
  });

  const { handleGoogle } = useOAuthButtons({
    onLoginSuccess: () => {
      () => {
        trackEvent({
          action: 'login_success',
        });
      };
    },
    onRegisterSuccess: () => {
      trackEvent({
        action: 'register_success',
      });
    },
  });

  useGoogleOneTap({
    enabled: isUtmExperimentEnabled && utmParams.authType === 'google',
    onResolve: (token) => {
      handleGoogle(token);
      setIsPromoModalVisible(false);
    },
  });

  const headerText = isUtmExperimentEnabled
    ? t('header.loginClaimOffer')
    : t('header.pickPlan');

  const isExperiment =
    PUBLIC_SWS_ENV !== 'prod' || plansVariant === 'experiment';

  const prefillEmailQuery = useQuery({
    queryKey: ['decryptedEmail', utmParams.prefillCode],
    queryFn: () => getDecryptedEmail(utmParams.prefillCode ?? ''),
    enabled:
      isUtmExperimentEnabled &&
      utmParams.authType === 'local' &&
      !!utmParams.prefillCode,
    retry: false,
  });

  /**
   * Streamlined promo modal that restricts user actions to either email login or Google login.
   * Used to help known auth type users quickly login to the platform without confusion about their
   * login type (e.g. google, email .etc).
   */
  const isStreamlinedPromoModalVisible =
    (utmParams.authType === 'local' || utmParams.authType === 'google') &&
    utmParams.group === UTM_GROUP.EASTER_PROMO &&
    !prefillEmailQuery.isFetching &&
    !authCodeLogin.isLoading &&
    isPromoModalVisible;

  /**
   * Registration promo modal that allows all 'standard' login and registration methods, but is
   * less streamlined UX-wise.
   * Used when we have no prior knowledge of the user's auth type or previous login.
   */
  const isRegistrationPromoModalVisible =
    !isStreamlinedPromoModalVisible &&
    !prefillEmailQuery.isFetching &&
    !authCodeLogin.isLoading &&
    isPromoModalVisible;

  return (
    <Track>
      <div className="w-full bg-p-white-100">
        <div className="flex flex-col items-center justify-center bg-[hsl(194_98_44/20%)]">
          <OneColumn>
            <DocumentHeader
              allowCrawlers
              title={t('title.pricingPlans')}
              description={t('description.pricingPlans')}
            >
              <link rel="canonical" href={`${SWS_HOST}/plans`} />
            </DocumentHeader>
            <div className="p-x2">
              <Navigation />
            </div>
            <Heading
              as="h1"
              size="mega"
              weight="strong"
              className="mt-x4 text-center text-p-grey-30"
            >
              {headerText}
            </Heading>
            <Plans />
          </OneColumn>
        </div>
        <CompareFeatures />
        {isExperiment && <Faqs />}
        <Row justifyContent="center">
          <OneColumn>
            <FeaturedOn />
          </OneColumn>
        </Row>
        <Reviews />
        {isRegistrationPromoModalVisible && (
          <GuestRegistrationModal
            initialType="login"
            canClose
            prefillEmail={prefillEmailQuery.data}
            focusOnMount={true}
            onClose={() => setIsPromoModalVisible(false)}
            onRegisterSuccess={() => setIsPromoModalVisible(false)}
            onLoginSuccess={() => setIsPromoModalVisible(false)}
            SplashPanel={
              utmParams.group === UTM_GROUP.EASTER_PROMO
                ? {
                    Component: EasterPromoLoginSplash,
                  }
                : undefined
            }
          />
        )}
        <Modal
          className="max-w-96 overflow-hidden [--s-theme:light] lg:max-w-[877px]"
          isOpen={isStreamlinedPromoModalVisible}
          isDismissable={false}
        >
          <div
            className="relative grid w-96 grid-cols-1 lg:w-[877px] lg:grid-cols-[7fr_5fr]"
            data-cy-id="guest-registration-modal"
          >
            <section>
              <EasterPromoLoginSplash
                className={
                  utmParams.authType === 'google' ? 'lg:min-h-[442px]' : ''
                }
              />
            </section>
            <section className="flex flex-col justify-center gap-x5 p-x3 md:p-x5">
              <div className="flex flex-col gap-x3">
                <Heading as="h2" size="xLarge" className="text-center">
                  {t('modal.signInGetDiscount')}
                </Heading>
                {utmParams.authType === 'local' && (
                  <LoginForm
                    prefillEmail={prefillEmailQuery.data}
                    focusOnMount={true}
                    onSuccess={() => setIsPromoModalVisible(false)}
                  />
                )}
                {utmParams.authType === 'google' && (
                  <ContinueWithGoogle
                    // Bit hacky with the important overrides, TODO: fix this as a cleaner
                    // passthrough by changing internal component to use a tailwind-enabled button.
                    className="!h-x6 !text-base [&>svg]:!h-x3 [&>svg]:!w-x3"
                    onSuccess={(token) => {
                      handleGoogle(token);
                      setIsPromoModalVisible(false);
                    }}
                  />
                )}
                <p className="m-0 text-center text-tiny text-softer">
                  <Translation i18nKey="app.modal.registration.footer.termsAndConditions">
                    By using Simply Wall St you are agreeing to our
                    <a
                      rel="noopener noreferrer"
                      href={SWS_TERMS_AND_CONDITIONS}
                      target="_blank"
                      data-cy-id="link-terms-and-conditions"
                      className="text-current"
                    >
                      terms and conditions.
                    </a>
                    Simply Wall St provides general investment advice only.
                  </Translation>
                </p>
              </div>
            </section>
            <Button
              variant="subtle"
              size="medium"
              className="absolute right-x2 top-x2 size-x4 p-0"
              aria-label={t('aria.close')}
              onClick={() => setIsPromoModalVisible(false)}
            >
              <CrossSolid className="fill-current" />
            </Button>
          </div>
        </Modal>
      </div>
      <OneColumn>
        <Footer />
      </OneColumn>
      {enableZendeskWidget && hasSeenPlansRef.current && <ZendeskWidget />}
    </Track>
  );
};

export default OurPlans;
